import { AppError } from "@shared/app-error";
import {
  type WorkerRequest,
  type WorkerResponse,
  type WorkerResult,
  workerResultToError,
} from "@shared/worker-protocol";

/**
 * §5.3 / §5.8.3: Worker 通信ラッパー (classic worker + in-flight 1 件直列化)。
 *
 * - id 付き correlation Map は使わない。 pending: 単一スロットを保持する
 * - 呼び出し側から見ると `call(req)` は普通に throw する関数 (Worker 境界の Result は内部に隠す)
 * - 直列化違反 (前 RPC 完了前に新規 call) は呼び出しロジックのバグなので
 *   `AppError('WorkerRpcInFlight', 'fatal')` を専用 code で投げる (Sentry 側で件数を直接見られる)
 * - worker.onerror / messageerror が発火した場合は再起動せず、 pending を
 *   `AppError('Unexpected', 'fatal')` で reject して呼び出し元にセッション終了させる
 * - `whenIdle()` で「現在の in-flight RPC が drain しきるまで」 を外から待てる。
 *   worker を跨いで再利用する loop handoff (PiP 切替) で新 loop の最初の RPC を
 *   前 loop の RPC drain 後に gate するための surface (issue #101 / R9)。
 *
 * 新規 class を作らない方針 (§5.2) に従い、 createWorkerClient() で関数 API を返す。
 */

export type WorkerClient = {
  call: <Req extends WorkerRequest>(
    request: Req,
    transfer?: Transferable[],
  ) => Promise<ResponseValueFor<Req>>;
  /**
   * in-flight RPC が無ければ即 resolve、 有れば現 RPC が settle (message 応答 / error /
   * messageerror / terminate のいずれか) した時点で resolve する。 loop handoff で「前 loop の
   * RPC が確実に drain しきってから新 loop の最初の RPC を撃つ」 gate に使う。
   *
   * §issue #101: deadline は持たない。 当初 2_000ms の deadline を置いたが、 MediaPipe の
   * 初回 detectForVideo は XNNPACK delegate 構築で 3 秒超かかることがあり、 deadline が
   * 「遅い worker」 を「固まった worker」 と誤判定して busy な worker に loop を発射させ
   * WorkerRpcInFlight を踏んだ。 worker が error / terminate したときも settle 経路で
   * flushIdleWaiters が走るため、 未 resolve になるのは alive な worker が事実上あり得ない
   * 内部 deadlock をした場合だけ。 in-flight 1 件直列化そのものは変えない (queue 化しない)。
   */
  whenIdle: () => Promise<void>;
  terminate: () => void;
};

type ResponseValueFor<Req extends WorkerRequest> =
  Extract<WorkerResponse, { method: Req["type"] }> extends { result: infer R }
    ? R extends { ok: true; value: infer V }
      ? V
      : never
    : never;

export function createWorkerClient(workerFactory: () => Worker): WorkerClient {
  const worker = workerFactory();
  let pending: { resolve: (value: unknown) => void; reject: (err: unknown) => void } | null = null;
  // `whenIdle()` が登録した待ち合わせ resolver。 pending が null になった時点で flush する。
  let idleWaiters: (() => void)[] = [];

  // pending RPC が settle して in-flight スロットが空いたことを idle 待ち合わせに伝える。
  // pending を null に戻す箇所 (message / error / messageerror / terminate) すべてで呼ぶ。
  const flushIdleWaiters = (): void => {
    if (idleWaiters.length === 0) return;
    const waiters = idleWaiters;
    idleWaiters = [];
    for (const notify of waiters) notify();
  };

  worker.addEventListener("message", (event: MessageEvent<WorkerResponse>) => {
    const current = pending;
    if (!current) {
      // 想定外の自発送信 (例: Worker 側のバグや log message)。 in-flight 1 件直列化の
      // 不変条件を破るためコンソールに記録する (production では Sentry 側で気付ける)。
      console.warn("[worker] unexpected message without pending RPC", event.data);
      return;
    }
    pending = null;
    flushIdleWaiters();
    try {
      // WorkerResponse は method 別の union のため、ここでは WorkerResult<unknown> として
      // 個別の T を捨てて取り扱う。 呼び出し側の戻り型は ResponseValueFor<Req> で復元する。
      const value = workerResultToError(event.data.result as WorkerResult<unknown>);
      current.resolve(value);
    } catch (err) {
      current.reject(err);
    }
  });

  worker.addEventListener("error", (event) => {
    const current = pending;
    pending = null;
    flushIdleWaiters();
    const err = new AppError(
      "Unexpected",
      "fatal",
      `worker errored: ${event.message ?? "unknown"}`,
      event.error,
    );
    current?.reject(err);
  });

  worker.addEventListener("messageerror", () => {
    const current = pending;
    pending = null;
    flushIdleWaiters();
    current?.reject(new AppError("Unexpected", "fatal", "worker messageerror"));
  });

  return {
    call(request, transfer) {
      if (pending) {
        throw new AppError(
          "WorkerRpcInFlight",
          "fatal",
          `RPC '${request.type}' issued while another RPC is in flight`,
        );
      }
      return new Promise((resolve, reject) => {
        pending = { resolve: resolve as (v: unknown) => void, reject };
        worker.postMessage(request, transfer ?? []);
      });
    },
    whenIdle() {
      if (!pending) return Promise.resolve();
      // 観測時点の in-flight RPC が settle した時点で resolve する。 flushIdleWaiters が
      // settle 経路すべて (message / error / messageerror / terminate) で走るため、 settle 後に
      // 新 RPC が発射されてもその RPC を待ち続けることはない。
      return new Promise<void>((resolve) => {
        idleWaiters.push(resolve);
      });
    },
    terminate() {
      const current = pending;
      pending = null;
      flushIdleWaiters();
      worker.terminate();
      // 意図的なシャットダウン (teardown / handleStop 経由) 由来の reject なので transient で十分。
      // frame-loop 側は teardown 中 (!running) かつ非 fatal なら catch を握り潰す方針 (§5.8.2)。
      current?.reject(new AppError("Unexpected", "transient", "worker terminated"));
    },
  };
}
